import * as cache from "../cache/index.js";
import { isSettledDuplicate } from "../core/message-reconcile.js";
import { channelState, dmState } from "../core/state.svelte.js";
import type { Message } from "../core/types.js";

export type SendSurface = "channel" | "dm";
type SendStatus = "pending" | "sent" | "failed";

// Bubbles of sends not yet echoed (#502), by `channel:<id>` / `dm:<peerId>`: the surface
// re-shows them after its history fetch, which would drop them. Removed on delivery.
export const pendingOutboxBubbles = new Map<string, Message[]>();
function outboxSurfaceKey(kind: SendSurface, targetId: string): string {
  return `${kind}:${targetId}`;
}

export function keepBubble(kind: SendSurface, targetId: string, msg: Message): void {
  const key = outboxSurfaceKey(kind, targetId);
  const kept = pendingOutboxBubbles.get(key) ?? [];
  if (!kept.some((m) => m.id === msg.id)) pendingOutboxBubbles.set(key, [...kept, msg]);
}

export function surfaceFor(surface: SendSurface): {
  setSendStatus: (id: string, s: SendStatus, reason?: string) => void;
  messages: { id: string }[];
} {
  return surface === "dm" ? dmState : channelState;
}

// Re-show the kept bubbles after a surface's fetch; idempotent (addMessage skips a known id).
export function reapplyKeptBubbles(
  kind: SendSurface,
  targetId: string,
  settle: (clientMsgId: string | null | undefined) => void,
): void {
  const bubbles = pendingOutboxBubbles.get(outboxSurfaceKey(kind, targetId));
  if (!bubbles || bubbles.length === 0) return;
  const list = () => (kind === "channel" ? channelState.messages : dmState.messages);
  for (const msg of bubbles) {
    // The page holds the server row: the send landed.
    if (isSettledDuplicate(list(), msg)) settle(msg.clientMsgId);
    // The kept copy owns the bubble's status: a cache-painted copy of it can be stale.
    else if (list().some((m) => m.id === msg.id && !sameBubbleStatus(m, msg))) {
      surfaceFor(kind).setSendStatus(msg.id, msg.sendStatus ?? "pending", msg.sendFailedReason);
    } else if (kind === "channel") channelState.addMessage(msg);
    else dmState.addMessage(msg);
  }
}

export function forgetOutboxBubble(clientMsgId: string): void {
  const localId = cache.localIdFor(clientMsgId);
  for (const [key, bubbles] of pendingOutboxBubbles.entries()) {
    const next = bubbles.filter((m) => m.id !== localId);
    if (next.length === 0) pendingOutboxBubbles.delete(key);
    else if (next.length !== bubbles.length) pendingOutboxBubbles.set(key, next);
  }
}

function sameBubbleStatus(live: Message, kept: Message): boolean {
  return live.sendStatus === kept.sendStatus && live.sendFailedReason === kept.sendFailedReason;
}

// The one status write for a send's bubble: its live row(s) and its kept copy together.
export function setBubbleStatus(
  surface: SendSurface | undefined,
  localId: string,
  status: SendStatus,
  reason?: string,
): void {
  for (const live of surface ? [surfaceFor(surface)] : [channelState, dmState]) {
    live.setSendStatus(localId, status, reason);
  }
  patchKeptBubble(localId, status, reason);
}

function patchKeptBubble(localId: string, status: SendStatus, reason: string | undefined): void {
  for (const [key, bubbles] of pendingOutboxBubbles.entries()) {
    const idx = bubbles.findIndex((m) => m.id === localId);
    if (idx < 0) continue;
    const next = bubbles.slice();
    next[idx] = {
      ...next[idx],
      sendStatus: status,
      ...(reason === undefined ? {} : { sendFailedReason: reason }),
    };
    pendingOutboxBubbles.set(key, next);
  }
}

// A send's row in the open list, with the channel or DM peer it went to.
export function openSendRow(
  surface: SendSurface,
  localId: string,
): { row: Message; targetId: string } | undefined {
  const list = surface === "dm" ? dmState.messages : channelState.messages;
  const row = list.find((m) => m.id === localId);
  const targetId = surface === "dm" ? row?.toId : row?.channelId;
  return row && targetId ? { row, targetId } : undefined;
}

// Leaving the surface drops the row; keep it so a return re-shows its status.
export function keepParkedBubble(surface: SendSurface, localId: string): void {
  const open = openSendRow(surface, localId);
  if (open) keepBubble(surface, open.targetId, open.row);
}

// A refusal of the text itself names its reason instead of Retry; any other code keeps Retry.
const CONTENT_REFUSAL_CODES: ReadonlySet<string> = new Set(["invalid_request"]);

// A DM send the relay refused at the DM-peer gate (an unknown id or an erased person: one gate, so one copy and no
// oracle). Any other refusal, Connect's "no shared channel" included, keeps its own copy and its Retry.
export const PERSON_DM_REFUSED = "You can't message this person.";
export function dmRefusalReason(
  surface: SendSurface | undefined,
  code: string | undefined,
  gate: string | undefined,
): string | undefined {
  return surface === "dm" && code === "forbidden" && gate === "dm_peer_unknown"
    ? PERSON_DM_REFUSED
    : undefined;
}

export function contentRefusalReason(
  code: string | undefined,
  message: string,
): string | undefined {
  return code !== undefined && CONTENT_REFUSAL_CODES.has(code) ? message : undefined;
}
